import { BrowserRouter, Navigate, Outlet, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./contexts/AuthContext";
import AppLayout from "./layouts/AppLayout";
import Login from "./pages/Login";
import Logout from "./pages/Logout";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Patients from "./pages/Patients";
import PatientDetail from "./pages/PatientDetail";
import Doctors from "./pages/Doctors";
import Services from "./pages/Services";
import Billing from "./pages/Billing";
import Reports from "./pages/Reports";
import Prescriptions from "./pages/Prescriptions";
import SamplesLabs from "./pages/SamplesLabs";
import ReportStandardizer from "./pages/ReportStandardizer";
import Employees from "./pages/Employees";
import Attendance from "./pages/Attendance";
import Payroll from "./pages/Payroll";
import Expenses from "./pages/Expenses";
import Analytics from "./pages/Analytics";
import Integrations from "./pages/Integrations";
import ImportCenter from "./pages/ImportCenter";
import Settings from "./pages/Settings";
import MobileMenu from "./pages/MobileMenu";
import Maintenance from "./pages/Maintenance";
import { MAINTENANCE_CONFIG } from "./config/maintenance";
import { useAuth } from "./contexts/AuthContext";
import { canAccessPath } from "./utils/permissions";
import "./App.css";

function ProtectedRoute() {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <Outlet />;
}

function RoleRoute({ path, children }) {
  const { user } = useAuth();
  if (!canAccessPath(user, path)) {
    return <Navigate to="/app" replace />;
  }
  return children;
}

function App() {
  if (MAINTENANCE_CONFIG.global) {
    return (
      <div className="App min-h-screen bg-hospital-bg">
        <BrowserRouter>
          <Maintenance isGlobal={true} />
        </BrowserRouter>
      </div>
    );
  }

  return (
    <div className="App min-h-screen bg-hospital-bg">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* ── Auth pages ── */}
            <Route path="/login" element={<Login />} />
            <Route path="/logout" element={<Logout />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* ── Protected app routes ── */}
            <Route element={<ProtectedRoute />}>
              <Route path="/" element={<AppLayout />}>
                <Route index element={<Dashboard />} />
                <Route path="patients" element={<RoleRoute path="/patients"><Patients /></RoleRoute>} />
                <Route path="patients/:id" element={<RoleRoute path="/patients"><PatientDetail /></RoleRoute>} />
                <Route path="doctors" element={<RoleRoute path="/doctors"><Doctors /></RoleRoute>} />
                <Route path="services" element={<RoleRoute path="/services"><Services /></RoleRoute>} />
                <Route path="samples" element={<RoleRoute path="/samples"><SamplesLabs /></RoleRoute>} />
                <Route path="report-extract" element={<RoleRoute path="/report-extract">{MAINTENANCE_CONFIG.reportExtract ? <Maintenance featureName="Report Extract" /> : <SamplesLabs />}</RoleRoute>} />
                <Route path="report-standardizer" element={<RoleRoute path="/report-standardizer">{MAINTENANCE_CONFIG.reportStandardizer ? <Maintenance featureName="Report Standardizer" /> : <ReportStandardizer />}</RoleRoute>} />
                <Route path="billing" element={<RoleRoute path="/billing"><Billing /></RoleRoute>} />
                <Route path="billing/:patientId" element={<RoleRoute path="/billing"><Billing /></RoleRoute>} />
                <Route path="reports" element={<RoleRoute path="/reports"><Reports /></RoleRoute>} />
                <Route path="prescriptions" element={<RoleRoute path="/prescriptions">{MAINTENANCE_CONFIG.prescriptions ? <Maintenance featureName="Prescriptions" /> : <Prescriptions />}</RoleRoute>} />
                <Route path="attendance" element={<RoleRoute path="/attendance"><Attendance /></RoleRoute>} />
                <Route path="employees" element={<RoleRoute path="/employees"><Employees /></RoleRoute>} />
                <Route path="payroll" element={<RoleRoute path="/payroll"><Payroll /></RoleRoute>} />
                <Route path="expenses" element={<RoleRoute path="/expenses"><Expenses /></RoleRoute>} />
                <Route path="analytics" element={<RoleRoute path="/analytics"><Analytics /></RoleRoute>} />
                <Route path="integrations" element={<RoleRoute path="/integrations">{MAINTENANCE_CONFIG.integrations ? <Maintenance featureName="Integrations" /> : <Integrations />}</RoleRoute>} />
                <Route path="imports" element={<RoleRoute path="/imports">{MAINTENANCE_CONFIG.importCenter ? <Maintenance featureName="Import Center" /> : <ImportCenter />}</RoleRoute>} />
                <Route path="menu" element={<RoleRoute path="/menu"><MobileMenu /></RoleRoute>} />
                <Route path="settings" element={<RoleRoute path="/settings"><Settings /></RoleRoute>} />
              </Route>
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors closeButton toastOptions={{ style: { fontFamily: "IBM Plex Sans, sans-serif" } }} />
      </AuthProvider>
    </div>
  );
}

export default App;
